/**
 * 登録済みのカードの画像の点検（Issue #930）:
 * - 型番が重複している画像: 同じカード・同じ型番が 2 枚以上の組。デッキで使っている数を見て、要らないものを消す（デッキは代わりの画像へ付け替え）
 * - 型番と言語が食い違っている画像: 型番の地域（SD01-JP001 の JP）と画像の言語が合わないもの（AE と地域の無い古い型番は除く）。
 *   型番を直すか、言語を付け替える（サーバーの `card_images.language_mismatches` / `set_language`）
 * 画像の行は `CardImageManager` の `ImageRow`（押すと大きく開く・既定・型番・削除）。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';
import { ImageRow } from './CardImageManager';

const PAGE = 30;
const LANG_LABEL = { jp: '日本語', en: 'English' };

function useList(action, refreshKey) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.project(action)
    .then((r) => setRows(Array.isArray(r) ? r : []))
    .catch(() => setRows([])), [action]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return [rows, load];
}

function Section({ title, note, count, testId, children, more }) {
  return (
    <div style={{ marginTop: 32 }} data-testid={testId}>
      <h2 style={{ fontSize: '1rem', marginBottom: 4, color: '#aaa' }}>{title} ({count}件)</h2>
      <div className="card-meta" style={{ marginBottom: 12 }}>{note}</div>
      {count === 0 && <p className="loading">ありません</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
      {more}
    </div>
  );
}

function More({ total, shown, setShown }) {
  if (total <= shown) return null;
  return (
    <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => setShown((n) => n + PAGE)}>
      もっと見る（残り {total - shown} 件）
    </button>
  );
}

const cardTitle = (r) => `${r.card_name ?? '不明'}（${r.card_id}）`;

export function DuplicateSetCodes({ refreshKey }) {
  const [groups, reload] = useList('GetDuplicateSetCodes', refreshKey);
  const [shown, setShown] = useState(PAGE);
  return (
    <Section title="型番が重複している画像" testId="duplicate-set-codes" count={groups.length}
      note="同じカード・同じ型番・同じレアリティの画像が 2 枚以上あります。レアリティが違うならレアリティを入れると分かれます。要らないものは消してください（使っているデッキは残した画像へ付け替えます）。"
      more={<More total={groups.length} shown={shown} setShown={setShown} />}>
      {groups.slice(0, shown).map((g) => (
        <div key={`${g.card_id}/${g.set_code}/${g.rarity}`} className="card" data-testid={`duplicate-${g.card_id}-${g.set_code}`}>
          <div className="card-title" style={{ marginBottom: 8 }}>
            {cardTitle(g)} — {g.set_code} {g.rarity ?? '（レアリティ未設定）'}（{g.images.length} 枚）
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {g.images.map((img) => <ImageRow key={img.id} img={img} allowLanguage onChanged={reload} />)}
          </div>
        </div>
      ))}
    </Section>
  );
}

export function LanguageMismatches({ refreshKey }) {
  const [rows, reload] = useList('GetSetCodeLanguageMismatches', refreshKey);
  const [shown, setShown] = useState(PAGE);
  return (
    <Section title="型番と言語が食い違っている画像" testId="language-mismatches" count={rows.length}
      note="型番の地域（SD01-JP001 の JP）と画像の言語が合いません（AE と地域の無い古い型番は除く）。型番を直すか、言語を付け替えてください。"
      more={<More total={rows.length} shown={shown} setShown={setShown} />}>
      {rows.slice(0, shown).map((img) => (
        <ImageRow key={img.id} img={img} allowLanguage onChanged={reload}
          heading={<>
            <strong>{cardTitle(img)}</strong> — 型番 {img.set_code} は
            {img.expected ? `${LANG_LABEL[img.expected]}` : `対応外の地域（${img.region}）`}、画像は {LANG_LABEL[img.language_code] ?? img.language_code}
          </>} />
      ))}
    </Section>
  );
}

export default function CardImageChecks({ refreshKey }) {
  return (
    <>
      <DuplicateSetCodes refreshKey={refreshKey} />
      <LanguageMismatches refreshKey={refreshKey} />
    </>
  );
}
